import { EQUIPO_CHAT_PATH } from "@/modules/equipo/equipoRoutes";
import { PANEL_GENERAL_EQUIPO_TAB } from "@/modules/panelGeneralTabs";

/** Rutas de retorno al salir de herramientas comunes (usuarios, chat interno). */
export const PANEL_GENERAL_PATH = "/";
export const DESPACHO_INICIO_PATH = "/despacho/inicio";
/** @deprecated use PANEL_GENERAL_EQUIPO_TAB — /despacho/auxiliares redirige al panel */
export const DESPACHO_AUXILIARES_PATH = PANEL_GENERAL_EQUIPO_TAB;

export const EQUIPO_RETURN_TO_SESSION_KEY = "vf:equipo_return_to";
export const RETURN_TO_QUERY_PARAM = "returnTo";

const ALLOWED_RETURN_PREFIXES = [
  "/",
  "/despacho/",
  "/equipo/",
  "/configuracion",
  "/despacho/configuracion",
];

/**
 * Valida ruta interna segura para volver tras alta/edición de usuario.
 * @param {string} path
 */
export function isValidEquipoReturnPath(path) {
  const p = String(path || "").trim().split("?")[0];
  if (!p.startsWith("/")) return false;
  if (p.startsWith("//")) return false;
  if (p.includes("..")) return false;
  if (p === PANEL_GENERAL_PATH) return true;
  return ALLOWED_RETURN_PREFIXES.some((prefix) => prefix !== "/" && p.startsWith(prefix));
}

/**
 * Herramientas comunes (/equipo/*): nunca regresar al módulo Gestión Despacho.
 * Permite Panel General o, desde usuarios, la página dedicada del chat interno.
 * @param {string} path
 */
export function normalizeCommonToolReturnTarget(path) {
  const p = String(path || "").trim().split("?")[0];
  if (!p || p === PANEL_GENERAL_PATH) return PANEL_GENERAL_PATH;
  if (p === EQUIPO_CHAT_PATH || p.startsWith(`${EQUIPO_CHAT_PATH}/`)) return EQUIPO_CHAT_PATH;
  if (p.startsWith("/despacho/")) return PANEL_GENERAL_PATH;
  return isValidEquipoReturnPath(p) ? p : PANEL_GENERAL_PATH;
}

/**
 * @param {string} [search]
 */
export function parseReturnToFromSearch(search = "") {
  try {
    const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
    const raw = String(params.get(RETURN_TO_QUERY_PARAM) || "").trim();
    return isValidEquipoReturnPath(raw) ? raw : "";
  } catch {
    return "";
  }
}

/**
 * Persiste returnTo solo cuando hay origen explícito (state/query al entrar).
 * @param {string} returnTo
 */
export function rememberEquipoReturnTarget(returnTo) {
  const path = String(returnTo || "").trim();
  if (!isValidEquipoReturnPath(path)) return;
  try {
    sessionStorage.setItem(EQUIPO_RETURN_TO_SESSION_KEY, path);
  } catch {
    /* ignore */
  }
}

/**
 * Resuelve destino al salir de herramienta común.
 * Prioridad: location.state.returnTo → query returnTo → fallback (Panel General).
 * No usa vf:active_work_module.
 * @param {{ state?: { returnTo?: string }, search?: string } | null} location
 * @param {string} [fallback="/"]
 */
export function resolveCommonToolReturnTarget(location, fallback = PANEL_GENERAL_PATH) {
  const fromState = String(location?.state?.returnTo || "").trim();
  if (isValidEquipoReturnPath(fromState)) {
    return normalizeCommonToolReturnTarget(fromState);
  }
  const fromQuery = parseReturnToFromSearch(location?.search);
  if (fromQuery) return normalizeCommonToolReturnTarget(fromQuery);
  const safeFallback = isValidEquipoReturnPath(fallback) ? fallback : PANEL_GENERAL_PATH;
  return normalizeCommonToolReturnTarget(safeFallback);
}

/** @deprecated alias — usar resolveCommonToolReturnTarget */
export function resolveEquipoReturnTarget(location) {
  return resolveCommonToolReturnTarget(location, PANEL_GENERAL_PATH);
}

/**
 * Etiqueta del botón volver según destino resuelto.
 * @param {{ state?: { returnTo?: string, returnLabel?: string }, search?: string } | null} location
 */
export function resolveCommonToolReturnLabel(location) {
  const target = resolveCommonToolReturnTarget(location);
  if (target === PANEL_GENERAL_PATH) return "Volver al Panel General";
  if (target === EQUIPO_CHAT_PATH) return "Volver al chat interno";
  if (target === DESPACHO_INICIO_PATH || target.startsWith("/despacho/")) {
    return "Volver a Gestión Despacho";
  }
  const custom = String(location?.state?.returnLabel || "").trim();
  if (custom) return custom;
  return "Volver";
}

/**
 * Captura contexto de regreso al montar pantalla común (state/query → ref + session).
 * @param {{ state?: object, search?: string } | null} location
 * @returns {string}
 */
export function captureCommonToolReturnContext(location) {
  const target = resolveCommonToolReturnTarget(location);
  const fromState = String(location?.state?.returnTo || "").trim();
  const fromQuery = parseReturnToFromSearch(location?.search);
  if (fromState || fromQuery) {
    rememberEquipoReturnTarget(target);
  }
  return target;
}

/**
 * State para abrir gestión de usuarios desde un origen conocido.
 * @param {string} returnTo
 * @param {{ returnLabel?: string, sourceModule?: string }} [options]
 * @returns {{ returnTo: string, returnLabel?: string, sourceModule?: string }}
 */
export function buildCommonToolReturnState(returnTo, options = {}) {
  const raw = isValidEquipoReturnPath(returnTo) ? returnTo : PANEL_GENERAL_PATH;
  const path = normalizeCommonToolReturnTarget(raw);
  rememberEquipoReturnTarget(path);
  const state = { returnTo: path };
  if (options.returnLabel) state.returnLabel = options.returnLabel;
  if (options.sourceModule) state.sourceModule = options.sourceModule;
  return state;
}

/** @deprecated alias — usar buildCommonToolReturnState */
export function buildEquipoUsuariosNavigationState(returnTo, options) {
  return buildCommonToolReturnState(returnTo, options);
}

/**
 * URL con query returnTo de respaldo (sobrevive recarga sin perder origen).
 * @param {string} basePath
 * @param {string} returnTo
 */
export function buildCommonToolPathWithReturn(basePath, returnTo) {
  const path = String(basePath || "").split("?")[0];
  const raw = isValidEquipoReturnPath(returnTo) ? returnTo : PANEL_GENERAL_PATH;
  const back = normalizeCommonToolReturnTarget(raw);
  return `${path}?${RETURN_TO_QUERY_PARAM}=${encodeURIComponent(back)}`;
}

/**
 * @param {import("react-router-dom").NavigateFunction} navigate
 * @param {{ state?: { returnTo?: string }, search?: string } | null} location
 * @param {string} [capturedReturnTo] — ref capturado al montar (prioridad sobre location stale)
 */
export function navigateCommonToolReturn(navigate, location, capturedReturnTo) {
  const raw =
    capturedReturnTo && isValidEquipoReturnPath(capturedReturnTo)
      ? capturedReturnTo
      : resolveCommonToolReturnTarget(location);
  const target = normalizeCommonToolReturnTarget(raw);
  try {
    sessionStorage.removeItem(EQUIPO_RETURN_TO_SESSION_KEY);
  } catch {
    /* ignore */
  }
  navigate(target, { replace: true });
}

/** @deprecated alias */
export function navigateEquipoReturn(navigate, location, capturedReturnTo) {
  navigateCommonToolReturn(navigate, location, capturedReturnTo);
}
